import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import CoinPayTypeEditModal from './CoinPayTypeEditModal';
import TableToggle from './TableToggle';
import {
  buildCoinPayTypePageParams,
} from './coinPayTypeConstants';

const EMPTY_FILTERS = {
  code: '',
  currency: '',
};

const TABLE_COLUMNS = [
  'ID',
  '代碼',
  '名稱',
  '幣別',
  '協議',
  '資產大類',
  '支付方式',
  '排序',
  '支援CNY',
  '支援USD',
  '三方錢包',
  '操作',
];

export default function CoinManagePage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.coinPayTypePage(buildCoinPayTypePageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setMessage('');
    loadRecords(1, reset);
  }

  function openCreate() {
    setEditRow(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditRow(row);
    setEditOpen(true);
    setMessage('');
  }

  async function handleSave(payload) {
    setSubmitting(true);
    setError('');
    try {
      if (payload.id) {
        await adminApi.updateCoinPayType(payload);
        setMessage('已更新');
      } else {
        await adminApi.createCoinPayType(payload);
        setMessage('已新增');
      }
      setEditOpen(false);
      setEditRow(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '保存失敗');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggle(row, field, nextValue, label) {
    if (!row?.id) {
      return;
    }
    const toggleKey = `${row.id}-${field}`;
    setTogglingKey(toggleKey);
    setError('');
    try {
      await adminApi.updateCoinPayType({ id: row.id, [field]: nextValue });
      setRecords((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, [field]: nextValue } : item))
      );
    } catch (err) {
      setError(err.message || `更新${label}失敗`);
    } finally {
      setTogglingKey(null);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteTarget?.id) {
      return;
    }
    setDeleting(true);
    setError('');
    try {
      await adminApi.removeCoinPayTypes([deleteTarget.id]);
      setDeleteTarget(null);
      setMessage('已刪除');
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>幣種管理</h2>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          新增幣種
        </button>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="cmCode">代碼</label>
          <input
            id="cmCode"
            type="text"
            value={filters.code}
            onChange={(e) => updateFilter('code', e.target.value)}
            placeholder="模糊搜尋"
          />
        </div>
        <div className="form-field">
          <label htmlFor="cmCurrency">幣別</label>
          <input
            id="cmCurrency"
            type="text"
            value={filters.currency}
            onChange={(e) => updateFilter('currency', e.target.value)}
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {!loading && (
        <p className="page-desc">共 {total} 筆。充提開關請至「線上支付通道」管理。</p>
      )}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td className="mono-cell">{row.code}</td>
                  <td>{row.name}</td>
                  <td>{row.currency}</td>
                  <td>{row.protocol || '—'}</td>
                  <td>{row.assetClass || '—'}</td>
                  <td>{row.payMethodKind || '—'}</td>
                  <td>{row.sort ?? 0}</td>
                  <td className="table-toggle-cell">
                    <TableToggle
                      checked={row.supportCny}
                      disabled={togglingKey === `${row.id}-supportCny`}
                      ariaLabel={`${row.code} 支援CNY`}
                      onChange={(v) => handleToggle(row, 'supportCny', v, '支援CNY')}
                    />
                  </td>
                  <td className="table-toggle-cell">
                    <TableToggle
                      checked={row.supportUsd}
                      disabled={togglingKey === `${row.id}-supportUsd`}
                      ariaLabel={`${row.code} 支援USD`}
                      onChange={(v) => handleToggle(row, 'supportUsd', v, '支援USD')}
                    />
                  </td>
                  <td className="table-toggle-cell">
                    <TableToggle
                      checked={row.isThirdPartyWallet}
                      disabled={togglingKey === `${row.id}-isThirdPartyWallet`}
                      ariaLabel={`${row.code} 三方錢包`}
                      onChange={(v) => handleToggle(row, 'isThirdPartyWallet', v, '三方錢包')}
                    />
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => openEdit(row)}
                      >
                        編輯
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setDeleteTarget(row)}
                      >
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />

      <CoinPayTypeEditModal
        open={editOpen}
        row={editRow}
        submitting={submitting}
        onClose={() => {
          if (!submitting) {
            setEditOpen(false);
            setEditRow(null);
          }
        }}
        onSubmit={handleSave}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除幣種"
        message={
          deleteTarget
            ? `確定刪除「${deleteTarget.name}」（${deleteTarget.code}）？若已被支付通道引用可能無法刪除。`
            : ''
        }
        confirmLabel={deleting ? '刪除中…' : '確認刪除'}
        confirming={deleting}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
